/**
 * Organization Service
 * Handles organization-related API calls including logo fetching.
 *
 * The logo is non-critical: every failure path returns null silently so the
 * UI falls back to a default. We intentionally do NOT log warnings/errors for
 * a missing logo (404), an HTML fallback response, or a network blip — those
 * are expected and would only spam the console.
 */
import { emitSessionExpired } from '../auth/sessionExpiry';

// The sidebar and the header both ask for the logo when the app shell mounts;
// a second caller while the first probe is still out shares it.
let inflightLogo = null;

export const organizationService = {
  /**
   * Get organization logo URL.
   * Returns null if no logo or on any error (non-critical, won't break UI).
   */
  async getLogoUrl(organizationId) {
    if (!organizationId) return null;

    const superadminBase =
      (typeof window !== 'undefined' && window.env && window.env.REACT_APP_SUPERADMIN_API_URL) ||
      process.env.REACT_APP_SUPERADMIN_API_URL ||
      '/api/v1';
    const normalizedBase = String(superadminBase).replace(/\/+$/, '');
    const checkEndpoint = `${normalizedBase}/organizations/${organizationId}/logo?format=json`;

    try {
      const response = await fetch(checkEndpoint, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
      });

      if (response.status === 401) {
        emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
        return null;
      }

      if (!response.ok) return null;

      // The logo endpoint may be unrouted in some deploys and fall through to the
      // SPA's index.html (HTML, 200). Only parse when it's actually JSON;
      // otherwise treat it as "no logo".
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return null;

      // The probe answers 200 with `logo_url: null` when the org has no logo — it used to
      // 404, which the browser logged as a failed resource on every page load and no
      // JavaScript could suppress. A null here is a definitive "no logo", so stop before
      // building a URL that would resolve to nothing.
      const payload = await response.json().catch(() => null);
      if (!payload || !payload.logo_url) return null;

      // Return a proxied image URL (relative path — nginx handles it). The
      // superadmin logo endpoint serves the file directly or redirects to a
      // signed URL, handling all storage backends. Cache-bust per minute.
      const cacheBuster = Math.floor(Date.now() / 60000);
      return `${normalizedBase}/organizations/${organizationId}/logo?t=${cacheBuster}`;
    } catch {
      // Network error / invalid response — logo is optional, fail silently.
      return null;
    }
  },

  /**
   * Get organization ID from localStorage.
   */
  getOrganizationId() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      const user = JSON.parse(userStr);
      return user.organization_id || user.org_id || user.organizationId || null;
    } catch {
      return null;
    }
  },

  /**
   * Get full logo URL for the current organization.
   */
  async getCurrentOrganizationLogo() {
    const orgId = this.getOrganizationId();
    if (!orgId) return null;
    if (inflightLogo && inflightLogo.orgId === orgId) return inflightLogo.promise;
    const promise = this.getLogoUrl(orgId).finally(() => {
      if (inflightLogo && inflightLogo.promise === promise) inflightLogo = null;
    });
    inflightLogo = { orgId, promise };
    return promise;
  },
};

export default organizationService;
